import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import PageShell from "@/components/PageShell";
import PageMeta from "@/components/PageMeta";
import AdSlot from "@/components/AdSlot";
import useJsonLd from "@/lib/useJsonLd";
import { findPost, posts } from "@/blog/posts";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = findPost(slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} — SendMe`;
    return () => { document.title = "SendMe — Share files with a 6-digit code"; };
  }, [post]);

  // Article + BreadcrumbList JSON-LD for search engines and AI assistants.
  const articleBodyText = post
    ? post.body.map((b) => b.text || (b.items ? b.items.join(" ") : "")).join("\n\n")
    : "";
  useJsonLd(
    "blog-article",
    post
      ? {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.cover ? [post.cover] : undefined,
          datePublished: post.date,
          dateModified: post.date,
          author: { "@type": "Organization", name: post.author || "SendMe Team", url: "https://sendme.co.in" },
          publisher: {
            "@type": "Organization",
            name: "SendMe",
            url: "https://sendme.co.in",
            logo: { "@type": "ImageObject", url: "https://sendme.co.in/logo192.png" },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://sendme.co.in/blog/${post.slug}`,
          },
          keywords: post.tags?.join(", "),
          articleBody: articleBodyText,
          wordCount: articleBodyText.trim().split(/\s+/).length,
          inLanguage: "en",
        }
      : null
  );
  useJsonLd(
    "blog-breadcrumb",
    post
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://sendme.co.in/" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://sendme.co.in/blog" },
            { "@type": "ListItem", position: 3, name: post.title, item: `https://sendme.co.in/blog/${post.slug}` },
          ],
        }
      : null
  );

  if (!post) {
    return (
      <PageShell eyebrow="404" title="Post not found." subtitle="That essay has wandered off." narrow>
        <Link to="/blog" className="btn-brut-primary">← Back to blog</Link>
      </PageShell>
    );
  }

  // adjacent
  const ordered = [...posts].sort((a, b) => b.date.localeCompare(a.date));
  const idx = ordered.findIndex((p) => p.slug === slug);
  const next = ordered[idx - 1];
  const prev = ordered[idx + 1];

  return (
    <article className="max-w-3xl mx-auto px-6 lg:px-10 py-10 md:py-14 fade-up" data-testid={`article-${post.slug}`}>
      <PageMeta
        title={post.title}
        description={post.excerpt}
        image={post.cover}
        type="article"
        publishedTime={post.date}
        modifiedTime={post.date}
        author={post.author}
        keywords={post.tags?.join(", ")}
      />
      <button
        onClick={() => navigate("/blog")}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-black/55 hover:text-[#FF3366] mb-6"
        data-testid="back-to-blog"
      >
        <ArrowLeft size={14} /> All posts
      </button>

      <div className="small-caps mb-3 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-1"><Calendar size={12}/> {fmtDate(post.date)}</span>
        <span className="flex items-center gap-1"><Clock size={12}/> {post.readTime}</span>
        <span>· by {post.author}</span>
      </div>

      <h1 className="text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-4">{post.title}</h1>
      <p className="text-base md:text-lg text-black/65 leading-relaxed mb-8">{post.excerpt}</p>

      <div className="aspect-[16/9] overflow-hidden border border-black mb-10">
        <img src={post.cover} alt="" className="w-full h-full object-cover" />
      </div>

      <div className="prose-content">
        {post.body.map((block, i) => (
          <div key={i}>
            <Block block={block} />
            {/* Insert one ad slot in the middle of long-form posts */}
            {i === Math.floor(post.body.length / 2) && (
              <AdSlot slot="" format="auto" />
            )}
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap gap-2 border-t border-black/10 pt-6">
        {post.tags.map((t) => (
          <span key={t} className="text-[10px] uppercase tracking-widest border border-black/15 px-2 py-1 font-mono">{t}</span>
        ))}
      </div>

      {/* prev/next */}
      <div className="mt-10 grid sm:grid-cols-2 gap-4">
        {prev ? (
          <Link to={`/blog/${prev.slug}`} className="brut-card p-4 brut-hover" data-testid="prev-post">
            <div className="small-caps mb-1"><ArrowLeft size={11} className="inline mr-1"/> Previous</div>
            <div className="font-bold leading-snug">{prev.title}</div>
          </Link>
        ) : <span />}
        {next ? (
          <Link to={`/blog/${next.slug}`} className="brut-card p-4 brut-hover text-right" data-testid="next-post">
            <div className="small-caps mb-1">Next <ArrowRight size={11} className="inline ml-1"/></div>
            <div className="font-bold leading-snug">{next.title}</div>
          </Link>
        ) : <span />}
      </div>

      {/* CTA */}
      <div className="mt-14 border border-black p-6 md:p-8 text-center">
        <div className="small-caps mb-3 text-[#FF3366]">Try it</div>
        <h3 className="text-2xl md:text-3xl tracking-tight mb-3">Send your next file the right way.</h3>
        <p className="text-black/65 mb-5 max-w-lg mx-auto">No sign-up. 6-digit code. Auto-expiry. Optional password.</p>
        <Link to="/" className="btn-brut-primary" data-testid="cta-send">Start sending <ArrowRight size={16} weight="bold"/></Link>
      </div>
    </article>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return <h2 className="text-2xl md:text-3xl tracking-tight mt-10 mb-4">{block.text}</h2>;
    case "p":
      return <p className="text-[17px] leading-[1.75] text-black/80 mb-5">{block.text}</p>;
    case "list":
      return (
        <ul className="mb-5 space-y-2 pl-1">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3 text-[16px] leading-relaxed text-black/80">
              <span className="text-[#FF3366] font-bold mt-0.5">→</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <blockquote className="my-7 border-l-4 border-[#FF3366] pl-5 py-1 text-lg italic text-black/85">
          {block.text}
        </blockquote>
      );
    case "code":
      return (
        <pre className="bg-black text-white p-4 my-5 overflow-x-auto text-sm font-mono">
          <code>{block.text}</code>
        </pre>
      );
    default:
      return null;
  }
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
